import React from 'react';
import { Calendar, Users, FileText, AlertCircle, Clock, Package } from 'lucide-react';
import { StatCard, Card, CardHeader, CardTitle, CardContent, Badge } from '@clinic-os/ui';
import { AreaChart } from '@blueforge-studio/charts';
import { useUpcomingAppointments } from '../../hooks/useAppointments';
import { usePatients } from '../../hooks/usePatients';
import { useInvoices } from '../../hooks/useInvoices';
import { useLowStockItems } from '../../hooks/useInventory';
import { Link } from 'react-router-dom';
import { DEMO_PATIENTS } from '../../data/demo-data';

// 7-day revenue trend (demo data — replace with real query when Supabase wired)
const REVENUE_TREND = [
  { date: 'Mon', revenue: 820 },
  { date: 'Tue', revenue: 1040 },
  { date: 'Wed', revenue: 760 },
  { date: 'Thu', revenue: 1320 },
  { date: 'Fri', revenue: 1180 },
  { date: 'Sat', revenue: 640 },
  { date: 'Sun', revenue: 420 },
];

const statusVariant: Record<string, 'success' | 'warning' | 'error' | 'default' | 'outline'> = {
  confirmed: 'success',
  scheduled: 'default',
  in_progress: 'warning',
  completed: 'outline',
  cancelled: 'error',
  no_show: 'error',
};

function getPatientName(patientId: string | undefined): string {
  if (!patientId) return 'Unknown';
  const patient = DEMO_PATIENTS.find((p) => p.id === patientId);
  return patient ? `${patient.firstName} ${patient.lastName}` : `Patient #${patientId}`;
}

export function DashboardPage() {
  const { data: appointments = [] } = useUpcomingAppointments(5);
  const { data: patients = [] } = usePatients();
  const { data: invoices = [] } = useInvoices();
  const { data: lowStockItems = [] } = useLowStockItems();

  const outstanding = invoices.filter((i) => i.status === 'sent' || i.status === 'overdue');
  const totalOutstanding = outstanding.reduce((sum, i) => sum + i.total, 0);

  const todayAppointments = appointments.filter(
    (a) => new Date(a.scheduledAt).toDateString() === new Date().toDateString()
  );

  const recentPatients = [...patients]
    .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime())
    .slice(0, 4);

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-slate-900">Dashboard</h1>
        <p className="text-sm text-slate-500 mt-1">Here's what's happening at your clinic today.</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <StatCard
          label="Today's Appointments"
          value={todayAppointments.length}
          icon={<Calendar className="w-5 h-5" />}
          change={`${appointments.length} upcoming this week`}
          changeType="positive"
        />
        <StatCard
          label="Active Patients"
          value={patients.length}
          icon={<Users className="w-5 h-5" />}
          change="All time"
          changeType="neutral"
        />
        <StatCard
          label="Outstanding"
          value={`$${totalOutstanding.toFixed(0)}`}
          icon={<FileText className="w-5 h-5" />}
          change={`${outstanding.length} invoices`}
          changeType={outstanding.length > 0 ? 'negative' : 'neutral'}
        />
        <StatCard
          label="Low Stock Items"
          value={lowStockItems.length}
          icon={<Package className="w-5 h-5" />}
          change={lowStockItems.length > 0 ? 'Needs reordering' : 'All stocked'}
          changeType={lowStockItems.length > 0 ? 'negative' : 'positive'}
        />
      </div>

      {/* Revenue trend */}
      <div className="mb-8">
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>Revenue — Last 7 Days</CardTitle>
              <span className="text-xs text-slate-500">Demo data</span>
            </div>
          </CardHeader>
          <CardContent>
            <AreaChart
              data={REVENUE_TREND}
              xKey="date"
              areas={[{ dataKey: 'revenue', name: 'Revenue', color: '#2563eb' }]}
              height={180}
              yAxisTickFormatter={(v: number) => `$${v}`}
            />
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Upcoming appointments */}
        <div className="lg:col-span-2">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Upcoming Appointments</CardTitle>
                <Link to="/appointments" className="text-sm text-blue-600 hover:underline">
                  View all
                </Link>
              </div>
            </CardHeader>
            <CardContent>
              {appointments.length === 0 ? (
                <div className="py-8 text-center text-slate-500">
                  <Calendar className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">No upcoming appointments</p>
                </div>
              ) : (
                <div className="divide-y divide-slate-100">
                  {appointments.map((apt) => (
                    <div key={apt.id} className="flex items-center gap-4 py-3 first:pt-0 last:pb-0">
                      <div className="flex-shrink-0 w-14 text-sm font-medium text-slate-500">
                        {new Date(apt.scheduledAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                        {' · '}
                        {new Date(apt.scheduledAt).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-slate-900">{apt.type}</p>
                        <p className="text-xs text-slate-500">{getPatientName(apt.patientId)}</p>
                      </div>
                      <Badge variant={statusVariant[apt.status]}>{apt.status.replace('_', ' ')}</Badge>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Alerts + recent patients */}
        <div className="space-y-6">
          {/* Alerts */}
          {(lowStockItems.length > 0 || outstanding.length > 0) && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <AlertCircle className="w-4 h-4 text-amber-500" />
                  Alerts
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {lowStockItems.slice(0, 2).map((item) => (
                  <div key={item.id} className="flex items-start gap-3 p-3 rounded-lg bg-amber-50 border border-amber-100">
                    <Package className="w-4 h-4 text-amber-700 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-sm font-medium text-amber-800">Low stock: {item.name}</p>
                      <p className="text-xs text-amber-700 mt-0.5">
                        {item.quantity} {item.unit} remaining (reorder at {item.lowStockThreshold})
                      </p>
                    </div>
                  </div>
                ))}
                {outstanding.length > 0 && (
                  <div className="flex items-start gap-3 p-3 rounded-lg bg-red-50 border border-red-100">
                    <Clock className="w-4 h-4 text-red-700 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-sm font-medium text-red-800">{outstanding.length} overdue invoices</p>
                      <p className="text-xs text-red-700 mt-0.5">Total: ${totalOutstanding.toFixed(2)}</p>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Recent Patients */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Recent Patients</CardTitle>
                <Link to="/patients" className="text-sm text-blue-600 hover:underline">
                  View all
                </Link>
              </div>
            </CardHeader>
            <CardContent>
              {recentPatients.length === 0 ? (
                <p className="text-sm text-slate-500">No patients yet</p>
              ) : (
                <div className="divide-y divide-slate-100">
                  {recentPatients.map((patient) => (
                    <div key={patient.id} className="flex items-center gap-3 py-3 first:pt-0 last:pb-0">
                      <Link
                        to={`/patients/${patient.id}`}
                        className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-700 text-sm font-medium flex-shrink-0 hover:bg-blue-200 transition-colors"
                      >
                        {patient.firstName[0]}{patient.lastName[0]}
                      </Link>
                      <div className="flex-1 min-w-0">
                        <Link
                          to={`/patients/${patient.id}`}
                          className="text-sm font-medium text-slate-900 hover:text-blue-600 truncate block"
                        >
                          {patient.firstName} {patient.lastName}
                        </Link>
                        <p className="text-xs text-slate-500">
                          {patient.allergies.length > 0 ? `${patient.allergies.length} allergies` : 'No allergies'}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
